import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AccountCommandError, newAccountBootstrap } from '@/services/vault/accountEnrollment'
import {
  firmRegistration,
  newFirmBootstrap,
  type FirmBootstrap,
  type FirmMfa,
  type FirmProgress,
} from '@/services/vault/firmRegistration'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm text-text-secondary space-y-1">
      <span>{label}</span>
      {children}
    </label>
  )
}

export default function FirmRegistrationPage() {
  const [bootstrap, setBootstrap] = useState<FirmBootstrap | null>(null)
  const [fields, setFields] = useState({
    firm: '',
    email: '',
    name: '',
    request: '',
    command: '',
    tracking: '',
    proof: '',
    username: '',
    password: '',
    confirm: '',
    code: '',
  })
  const [progress, setProgress] = useState<FirmProgress | null>(null)
  const [mfa, setMfa] = useState<FirmMfa | null>(null)
  const [phase, setPhase] = useState('')
  const [saved, setSaved] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [error, setError] = useState('')
  const [expired, setExpired] = useState(false)
  const [busy, setBusy] = useState(false)
  const pending = useRef<(() => Promise<void>) | null>(null)
  const alive = useRef(true)
  const running = useRef(false)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      pending.current = null
    }
  }, [])
  const change = (key: keyof typeof fields, value: string) =>
    setFields(v => ({ ...v, [key]: value }))
  const apply = (p: FirmProgress) => {
    setExpired(false)
    setProgress(p)
    if (p.terms_version !== progress?.terms_version) setAccepted(false)
    if (p.registration_status) setPhase(p.registration_status)
  }
  const execute = async (action: () => Promise<void>, readOnly = false) => {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError('')
    try {
      await action()
      if (alive.current && !readOnly) pending.current = null
    } catch (e) {
      if (alive.current) {
        setError(e instanceof Error ? e.message : 'Request unavailable')
        if (e instanceof AccountCommandError && e.status === 410) setExpired(true)
        if (!readOnly && e instanceof AccountCommandError && e.uncertain) pending.current = action
      }
    } finally {
      running.current = false
      if (alive.current) setBusy(false)
    }
  }
  const create = () => {
    const b = bootstrap ?? newFirmBootstrap()
    setBootstrap(b)
    const body = {
      ...b,
      firm_name: fields.firm,
      owner_email: fields.email,
      owner_name: fields.name,
      tenant_type: 'solo_ca' as const,
      plan_code: 'trial' as const,
    }
    void execute(async () => {
      try {
        const result = await firmRegistration.create(body)
        if (alive.current) setPhase(result.status)
      } catch (e) {
        if (alive.current && e instanceof AccountCommandError && !e.uncertain) setBootstrap(null)
        throw e
      }
    })
  }
  const context = () =>
    bootstrap ?? {
      request_id: fields.request.trim(),
      command_id: fields.command.trim(),
      bootstrap_secret: fields.tracking.trim(),
    }
  const check = () => {
    const b = context()
    setBootstrap(b)
    void execute(async () => {
      const p = await firmRegistration.status(b)
      if (alive.current) apply(p)
    }, true)
  }
  const verify = () => {
    const b = context()
    setBootstrap(b)
    const body = { ...b, email_proof: fields.proof.trim() }
    void execute(async () => {
      const p = await firmRegistration.verify(body)
      if (alive.current) apply(p)
    })
  }
  const setup = () => {
    if (fields.password !== fields.confirm) {
      setError('Passwords must match.')
      return
    }
    const b = context()
    const body = {
      ...b,
      email_proof: fields.proof.trim(),
      username: fields.username,
      password: fields.password,
      full_name: fields.name,
    }
    void execute(async () => {
      const result = await firmRegistration.setup(body)
      if (alive.current) {
        setMfa(result)
        setProgress(p => (p ? { ...p, status: 'mfa_setup' } : p))
        setFields(f => ({ ...f, password: '', confirm: '', proof: '' }))
      }
    })
  }
  const finish = () => {
    if (!progress || !saved || !accepted) return
    const body = {
      ...context(),
      mfa_code: fields.code,
      accepted: true as const,
      terms_version: progress.terms_version,
    }
    void execute(async () => {
      const p = await firmRegistration.finish(body)
      if (alive.current) {
        apply(p)
        setMfa(null)
        setFields(f => ({ ...f, code: '', password: '' }))
      }
    })
  }
  const authenticate = (activate: boolean) => {
    const body = { ...context(), password: fields.password, mfa_code: fields.code }
    void execute(async () => {
      const result = await (activate
        ? firmRegistration.activate(body)
        : firmRegistration.accept(body))
      if (alive.current) {
        setPhase(result.status)
        setFields(f => ({ ...f, password: '', code: '' }))
        if (result.status === 'activated') {
          setBootstrap(null)
          pending.current = null
          setFields({
            firm: '',
            email: '',
            name: '',
            request: '',
            command: '',
            tracking: '',
            proof: '',
            username: '',
            password: '',
            confirm: '',
            code: '',
          })
        }
      }
    })
  }
  const resend = () => {
    const b = context()
    void execute(async () => {
      const p = await firmRegistration.resend(b)
      if (alive.current) {
        apply(p)
        change('proof', '')
      }
    })
  }
  const resume = () => {
    const previous = context()
    const b = { request_id: previous.request_id, ...newAccountBootstrap() }
    const body = { ...b, email: fields.email, previous_command_id: previous.command_id }
    void execute(async () => {
      const p = await firmRegistration.resume(body)
      if (alive.current) {
        setBootstrap(b)
        apply(p)
        setMfa(null)
        setSaved(false)
        setAccepted(false)
        setFields(f => ({ ...f, proof: '', password: '', confirm: '', code: '' }))
      }
    })
  }
  const locked = busy || !!pending.current
  return (
    <main className="mx-auto max-w-xl p-6 space-y-5">
      <Link to="/login" className="text-link">
        Back to sign in
      </Link>
      <h1 className="text-2xl font-semibold">Firm registration</h1>
      <p className="text-sm text-muted">
        The owner verifies email, chooses credentials and enables an authenticator before firm
        activation. This grants no client-data ownership or access.
      </p>
      <p className="text-sm text-muted">
        Keep this page open. Private proof and credentials stay in memory and are cleared when you
        leave.
      </p>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      {phase && <p role="status">Registration: {phase.replace(/_/g, ' ')}</p>}
      {phase === 'activated' ? (
        <p>
          Your firm is activated.{' '}
          <Link to="/login" className="text-link">
            Sign in with a fresh authenticator code.
          </Link>
        </p>
      ) : (
        <>
          {!bootstrap && (
            <section className="card p-4 space-y-3">
              <h2 className="font-semibold">Request a new firm</h2>
              <Field label="Firm name">
                <input
                  className="input w-full"
                  value={fields.firm}
                  disabled={locked}
                  onChange={e => change('firm', e.target.value)}
                />
              </Field>
              <Field label="Owner name">
                <input
                  className="input w-full"
                  value={fields.name}
                  disabled={locked}
                  onChange={e => change('name', e.target.value)}
                />
              </Field>
              <Field label="Owner email">
                <input
                  type="email"
                  className="input w-full"
                  value={fields.email}
                  disabled={locked}
                  onChange={e => change('email', e.target.value)}
                />
              </Field>
              <p className="text-sm text-muted">
                Solo practice, 14-day trial beginning at activation.
              </p>
              <button
                className="btn btn-primary"
                disabled={locked || !fields.firm || !fields.email || !fields.name}
                onClick={create}
              >
                Request registration
              </button>
              <h2 className="font-semibold">Continue from an owner email</h2>
              <Field label="Firm registration reference">
                <input
                  className="input w-full"
                  value={fields.request}
                  disabled={locked}
                  onChange={e => change('request', e.target.value)}
                />
              </Field>
              <Field label="Owner command reference">
                <input
                  className="input w-full"
                  value={fields.command}
                  disabled={locked}
                  onChange={e => change('command', e.target.value)}
                />
              </Field>
              <Field label="Bootstrap from email">
                <input
                  className="input w-full"
                  value={fields.tracking}
                  disabled={locked}
                  onChange={e => change('tracking', e.target.value)}
                />
              </Field>
              <button
                className="btn btn-secondary"
                disabled={locked || !fields.request || !fields.command || !fields.tracking}
                onClick={check}
              >
                Check owner request
              </button>
            </section>
          )}
          {bootstrap && (
            <section className="card p-4 space-y-3">
              <p className="text-sm break-all">Reference: {bootstrap.request_id}</p>
              {progress?.firm_name && (
                <p>
                  Firm: {progress.firm_name} · Plan: {progress.plan_code}
                </p>
              )}
              <button className="btn btn-secondary" disabled={busy} onClick={check}>
                Check status
              </button>
              {(!progress || progress.status === 'awaiting_email') && (
                <>
                  <Field label="Email proof">
                    <input
                      className="input w-full"
                      value={fields.proof}
                      disabled={locked}
                      onChange={e => change('proof', e.target.value)}
                    />
                  </Field>
                  <button
                    className="btn btn-primary"
                    disabled={locked || !fields.proof}
                    onClick={verify}
                  >
                    Verify email
                  </button>
                  <button className="btn btn-secondary" disabled={locked} onClick={resend}>
                    Request replacement email
                  </button>
                </>
              )}
              {(progress?.status === 'email_verified' ||
                (progress?.status === 'mfa_setup' && !mfa)) && (
                <>
                  <Field label="Email proof">
                    <input
                      className="input w-full"
                      value={fields.proof}
                      disabled={locked}
                      onChange={e => change('proof', e.target.value)}
                    />
                  </Field>
                  <Field label="Owner name">
                    <input
                      className="input w-full"
                      value={fields.name}
                      disabled={locked}
                      onChange={e => change('name', e.target.value)}
                    />
                  </Field>
                  <Field label="Username">
                    <input
                      className="input w-full"
                      value={fields.username}
                      disabled={locked}
                      onChange={e => change('username', e.target.value)}
                    />
                  </Field>
                  <Field label="Password">
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="input w-full"
                      value={fields.password}
                      disabled={locked}
                      onChange={e => change('password', e.target.value)}
                    />
                  </Field>
                  <Field label="Confirm password">
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="input w-full"
                      value={fields.confirm}
                      disabled={locked}
                      onChange={e => change('confirm', e.target.value)}
                    />
                  </Field>
                  <button
                    className="btn btn-primary"
                    disabled={
                      locked ||
                      !fields.proof ||
                      !fields.password ||
                      !fields.username ||
                      !fields.name
                    }
                    onClick={setup}
                  >
                    Set up authenticator
                  </button>
                </>
              )}
              {((progress?.status === 'mfa_setup' && !mfa) || expired) && (
                <>
                  <p className="text-sm text-muted">
                    Resume preserves your original pending credentials and authenticator. Use the
                    original owner email; the server checks that pending setup exists.
                  </p>
                  <Field label="Email for pending setup resume">
                    <input
                      type="email"
                      className="input w-full"
                      value={fields.email}
                      disabled={busy || (locked && !expired)}
                      onChange={e => change('email', e.target.value)}
                    />
                  </Field>
                  <button
                    className="btn btn-secondary"
                    disabled={busy || (locked && !expired) || !fields.email}
                    onClick={resume}
                  >
                    Resume original pending setup
                  </button>
                </>
              )}
              {mfa && progress && (
                <>
                  <p>
                    Add this secret to your authenticator:{' '}
                    <code className="break-all">{mfa.secret}</code>
                  </p>
                  <p className="text-sm break-all">Recovery codes: {mfa.backup_codes.join(', ')}</p>
                  <label className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={saved}
                      onChange={e => setSaved(e.target.checked)}
                    />
                    I saved my recovery codes.
                  </label>
                  <label className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={e => setAccepted(e.target.checked)}
                    />
                    I accept account terms {progress.terms_version}.
                  </label>
                  <Field label="Authenticator code">
                    <input
                      className="input w-full"
                      value={fields.code}
                      onChange={e => change('code', e.target.value)}
                    />
                  </Field>
                  <button
                    className="btn btn-primary"
                    disabled={locked || !saved || !accepted || !fields.code}
                    onClick={finish}
                  >
                    Complete owner account
                  </button>
                </>
              )}
              {progress?.status === 'complete' && (
                <>
                  <p className="text-sm text-muted">
                    Use your password and a fresh authenticator or recovery code. Provisioning
                    starts after owner acceptance; check status until verified, then activate.
                  </p>
                  <Field label="Password">
                    <input
                      type="password"
                      autoComplete="current-password"
                      className="input w-full"
                      value={fields.password}
                      disabled={locked}
                      onChange={e => change('password', e.target.value)}
                    />
                  </Field>
                  <Field label="Fresh MFA code">
                    <input
                      className="input w-full"
                      value={fields.code}
                      disabled={locked}
                      onChange={e => change('code', e.target.value)}
                    />
                  </Field>
                  <button
                    className="btn btn-primary"
                    disabled={
                      locked ||
                      !fields.password ||
                      !fields.code ||
                      !['awaiting_owner', 'held'].includes(phase)
                    }
                    onClick={() => authenticate(false)}
                  >
                    Accept firm registration
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={locked || !fields.password || !fields.code || phase !== 'verified'}
                    onClick={() => authenticate(true)}
                  >
                    Activate verified firm
                  </button>
                </>
              )}
            </section>
          )}
        </>
      )}
      {pending.current && (
        <button
          className="btn btn-secondary"
          disabled={busy}
          onClick={() => {
            const action = pending.current
            if (action) void execute(action)
          }}
        >
          Retry exact command
        </button>
      )}
    </main>
  )
}
